import 'satellite-polyfills';

import { createApp } from 'vue';
import Notifications from '@kyvg/vue3-notification';
import velocity from 'velocity-animate';
import { plugin as featureFlagPlugin } from '@loadsmart/vue-feature-flags';
import { get, template } from 'lodash';

import initSentry from '@satellite/plugins/sentry';
import { analyticsKey } from '@satellite/composables/useAnalytics';
import { HTTP_CLIENT_KEY } from '@satellite/composables/useHttpClient';
import { setTranslator } from '@satellite/plugins/translator';

import App from './App.vue';
import { createJunoAnalyticsClient } from '@/analytics/client';
import router from './router';
import config from './config';
import apiClient from './services/apiClient';

import vuetify from './plugins/vuetifyPlugin';
import { createFeatureFlagClient } from './plugins/featureFlagPlugin';
import i18n from './plugins/i18n';
import { applyInitialJunoLocale } from '@/lib/junoLocale';

import { customFormsEn } from './locales/en/customForms';

// Satellite custom forms: i18n for validation messages
setTranslator((key: string, params?: Record<string, string | number>) => {
  const value = get({ customForms: { ...customFormsEn } }, key);

  if (typeof value !== 'string') {
    return key;
  }

  if (params) {
    const compiled = template(value, { interpolate: /\{([^}]+)\}/g });
    return compiled(params);
  }

  return value;
}, undefined);

const initApp = async () => {
  const app = createApp(App);

  // Satellite custom forms: HTTP client for API calls
  app.provide(HTTP_CLIENT_KEY, apiClient);

  const analyticsClient = createJunoAnalyticsClient();
  app.provide(analyticsKey, analyticsClient);

  app.use(router);
  app.use(vuetify);
  app.use(Notifications, { velocity });
  app.use(featureFlagPlugin, { unleashClient: createFeatureFlagClient() });
  app.use(i18n);

  initSentry(app, router, config);

  await router.isReady();
  try {
    await applyInitialJunoLocale(router, i18n);
  } catch (error) {
    console.error('Failed to apply initial locale:', error);
  } finally {
    app.mount('#app');
  }
};

initApp();
